import { z } from 'zod';

/**
 * Wave 10 — scoped portal tokens.
 *
 * Replaces `owner_invitations` for Wave 10 onwards. One row = one
 * shareable link + scope. Raw token lives in the URL; we persist only a
 * sha-256 hash.
 *
 * `role`:
 *   owner — whole-project, all progress + finance (invoice visibility
 *           still controlled by the owner portal's own UI toggles).
 *   buyer — unit-scoped; `scope.unitId` required. Sees only progress
 *           stages that affect the unit, and any comments flagged
 *           visibility=buyer.
 *
 * Lifecycle: created → (optionally) revoked. `expires_at` is a hard
 * wall; revoked_at is soft (revoker kept it around for audit).
 */

export const externalLinkRoleSchema = z.enum(['owner', 'buyer']);
export type ExternalLinkRole = z.infer<typeof externalLinkRoleSchema>;

export const externalLinkScopeSchema = z
  .object({
    unitId: z.string().optional(),
  })
  .strict();
export type ExternalLinkScope = z.infer<typeof externalLinkScopeSchema>;

export const externalLinkSchema = z.object({
  id: z.string(),
  tenantId: z.string(),
  projectId: z.string(),
  role: externalLinkRoleSchema,
  scope: externalLinkScopeSchema,
  // token_hash never leaves the server; the `token` field below is set only
  // on the initial mint response so the caller can copy/send the URL.
  recipientEmail: z.string().nullable(),
  recipientPhone: z.string().nullable(),
  createdBy: z.string(),
  expiresAt: z.string(),
  revokedAt: z.string().nullable(),
  lastViewedAt: z.string().nullable(),
  viewCount: z.number().int().nonnegative(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type ExternalLink = z.infer<typeof externalLinkSchema>;

export const createExternalLinkRequestSchema = z.object({
  role: externalLinkRoleSchema,
  scope: externalLinkScopeSchema.optional(),
  recipientEmail: z.string().email().optional(),
  recipientPhone: z.string().trim().max(30).optional(),
  ttlDays: z.number().int().min(1).max(365).default(30),
});
export type CreateExternalLinkRequest = z.infer<
  typeof createExternalLinkRequestSchema
>;

/**
 * Mint response carries the raw token — return to the caller exactly once
 * so they can copy the URL. Never persisted in plain text.
 */
export const createExternalLinkResponseSchema = externalLinkSchema.extend({
  token: z.string(),
  url: z.string(),
});
export type CreateExternalLinkResponse = z.infer<
  typeof createExternalLinkResponseSchema
>;

/**
 * Public `/v1/c/:token` payload — what the portal renders. Buyer scope
 * trims the stages list server-side, so the shape is the same regardless
 * of role; the frontend branches on `role` for the UI chrome.
 */
export const externalLinkProjectViewSchema = z.object({
  role: externalLinkRoleSchema,
  scope: externalLinkScopeSchema,
  project: z.object({
    id: z.string(),
    name: z.string(),
    clientName: z.string().nullable(),
    status: z.string(),
    startedOn: z.string().nullable(),
    expectedEndOn: z.string().nullable(),
    coverPhotoUrl: z.string().nullable(),
    progressPercent: z.number(),
  }),
  stages: z.array(
    z.object({
      id: z.string(),
      title: z.string(),
      status: z.string(),
      progressPercent: z.number(),
      plannedStart: z.string().nullable(),
      plannedEnd: z.string().nullable(),
    }),
  ),
  recentLogs: z.array(
    z.object({
      id: z.string(),
      logDate: z.string(),
      author: z.string(),
      summary: z.string().nullable(),
      coverPhotoUrl: z.string().nullable(),
    }),
  ),
  lastViewedAt: z.string().nullable(),
});
export type ExternalLinkProjectView = z.infer<
  typeof externalLinkProjectViewSchema
>;
